import type { Row, Table } from '@tanstack/react-table';
import * as React from 'react';

import { Box } from '../../components/ui/box';
import { Checkbox } from '../../components/ui/checkbox';
import { eventShiftKey } from '../../lib/event-modifiers.ts';
import {
  useDataTableGroupRows,
  useDataTableGroupKnownEmpty,
  useDataTableShiftSelect,
  useDataTableSelectAll,
  useDataTableSharedSelectAll,
} from './data-table-context';
import { DataTableRowDragGrip } from './data-table-row-drag';
import type { DataTableColumnDef } from './data-table-types';
import { SELECTION_COLUMN_WIDTH, pressArmedClosest } from './data-table-utils';

/**
 * Selection header checkbox component.
 * When rendered inside a group context, scopes select-all to that group's rows.
 * Otherwise falls back to all page rows.
 *
 * Supports three states:
 * - Unchecked: No rows selected
 * - Indeterminate: Some rows selected
 * - Checked: All rows selected
 */
function SelectionHeader<TData>({ table }: { table: Table<TData> }) {
  "use no memo";

  const groupRows = useDataTableGroupRows<TData>();
  const groupKnownEmpty = useDataTableGroupKnownEmpty();
  const { isSelectAll, setSelectAll } = useDataTableSelectAll();
  // Non-null only in a grouped `sharedHeader` table: this header is the ONLY
  // one rendered, so its checkbox addresses every group through the outer
  // table's cross-group state instead of the local group's rows.
  const sharedSelectAll = useDataTableSharedSelectAll();

  let isAllSelected: boolean;
  let isSomeSelected: boolean;
  let handleToggle: (value: boolean) => void;

  // Global selection count includes off-page selections that survive page eviction
  const globalSelectedCount = Object.values(table.getState().rowSelection).filter(Boolean).length;

  // Only selectable rows participate — rows the viewer can't act on have no
  // checkbox, so they neither count toward "all selected" nor get toggled.
  const selectableGroupRows = groupRows?.filter((r) => r.getCanSelect());

  // Omit the select-all checkbox when there is nothing to select: the group
  // settled at zero entities, or its loaded rows are all non-selectable. A
  // group with UNLOADED rows (totalCount > 0, none fetched) keeps it —
  // select-all mode covers off-page rows. Skipped in shared mode: the
  // hoisted header speaks for all groups, not the one it mounts inside.
  if (
    !sharedSelectAll &&
    groupRows &&
    (groupKnownEmpty ||
      (groupRows.length > 0 && selectableGroupRows!.length === 0))
  ) {
    return null;
  }

  if (sharedSelectAll) {
    isAllSelected = sharedSelectAll.isAllSelected;
    isSomeSelected = sharedSelectAll.isSomeSelected;
    handleToggle = sharedSelectAll.toggle;
  } else if (selectableGroupRows && selectableGroupRows.length > 0) {
    // Group-scoped selection
    const visibleSelectedCount = selectableGroupRows.filter((r) => r.getIsSelected()).length;
    isAllSelected = isSelectAll || (visibleSelectedCount === selectableGroupRows.length && globalSelectedCount === visibleSelectedCount);
    isSomeSelected = globalSelectedCount > 0 && !isAllSelected;
    handleToggle = (value: boolean) => {
      if (!value) {
        // Clear all selections including off-page rows
        table.setRowSelection({});
        setSelectAll(false);
      } else {
        table.setRowSelection((old) => {
          const next = { ...old };
          for (const row of selectableGroupRows) {
            next[row.id] = true;
          }
          return next;
        });
        setSelectAll(true);
      }
    };
  } else {
    // Fallback: all page rows
    isAllSelected = isSelectAll || (table.getIsAllPageRowsSelected() && globalSelectedCount === table.getRowModel().rows.length);
    isSomeSelected = globalSelectedCount > 0 && !isAllSelected;
    handleToggle = (value: boolean) => {
      if (!value) {
        table.setRowSelection({});
        setSelectAll(false);
      } else {
        table.toggleAllPageRowsSelected(true);
        setSelectAll(true);
      }
    };
  }

  // An unchecked header box is hover-revealed chrome on the header scope;
  // once anything is selected it stands. On touch there is no hover, so the
  // box appears with the first selection — never as a stray in an empty lane.
  // While hidden on a coarse pointer it is also inert: an invisible
  // select-all sitting a few pixels above the first row would otherwise
  // catch the tap meant to highlight that row and select everything.
  const hideUntilHover = !isAllSelected && !isSomeSelected;

  return (
    <Box
      width={16}
      height={16}
      align="center"
      justify="center"
      hoverReveal={hideUntilHover ? 'header' : undefined}
      className={hideUntilHover ? 'pointer-coarse:pointer-events-none' : undefined}
    >
      <Checkbox
        checked={isAllSelected}
        indeterminate={isSomeSelected}
        onCheckedChange={(value) => handleToggle(!!value)}
        aria-label={
          isAllSelected
            ? 'Deselect all rows'
            : isSomeSelected
              ? 'Select all rows (some selected)'
              : 'Select all rows'
        }
      />
    </Box>
  );
}

/**
 * Selection cell checkbox component.
 * Handles individual row selection.
 *
 * Uses `table.setRowSelection()` directly instead of `row.toggleSelected()`
 * to avoid TanStack's internal `getRow()` lookup — cached rows from
 * off-screen pages aren't in the current row model and would throw.
 */
function SelectionCell<TData>({ row, table }: { row: Row<TData>; table: Table<TData> }) {
  "use no memo";

  const canSelect = row.getCanSelect();
  const rowSelected = row.getIsSelected();

  // Non-selectable rows render no checkbox at all — the viewer can't act on
  // this row, so the selection affordance is omitted entirely (not disabled).
  // Hooks above this point keep the call order stable across rows.
  const omitCheckbox = !canSelect;
  const groupRows = useDataTableGroupRows<TData>();
  const lastSelectedIdRef = useDataTableShiftSelect();
  const { isSelectAll, setSelectAll } = useDataTableSelectAll();

  // row.getIsSelected() is false for off-page rows evicted from the row model;
  // isSelectAll covers those so the checkbox stays checked.
  const isSelected = rowSelected || isSelectAll;
  const hideUntilHover = !isSelected;

  const handleSelectionChange = React.useCallback(
    (value: boolean, shiftKey: boolean) => {
      // Deselecting a row breaks the "select all" state for this group.
      // Individually select all other loaded rows so they stay checked.
      if (!value && isSelectAll) {
        setSelectAll(false);
        if (groupRows) {
          table.setRowSelection((old) => {
            const next = { ...old };
            for (const r of groupRows) {
              if (r.id !== row.id) next[r.id] = true;
            }
            delete next[row.id];
            return next;
          });
          if (lastSelectedIdRef) lastSelectedIdRef.current = row.id;
          return;
        }
      }

      if (shiftKey && lastSelectedIdRef?.current != null && groupRows) {
        if (row.getIsSelected()) {
          // Shift-clicking an already selected row just deselects it
          table.setRowSelection((old) => {
            const next = { ...old };
            delete next[row.id];
            return next;
          });
        } else {
          const lastIdx = groupRows.findIndex(
            (r) => r.id === lastSelectedIdRef.current,
          );
          const currentIdx = groupRows.findIndex((r) => r.id === row.id);

          if (lastIdx !== -1 && currentIdx !== -1) {
            const start = Math.min(lastIdx, currentIdx);
            const end = Math.max(lastIdx, currentIdx);
            table.setRowSelection((old) => {
              const next = { ...old };
              for (const row of groupRows.slice(start, end + 1)) {
                next[row.id] = true;
              }
              return next;
            });
          } else {
            // Anchor row is on a different page — fall back to normal toggle
            // and reset anchor to this row
            table.setRowSelection((old) => ({ ...old, [row.id]: true }));
            lastSelectedIdRef.current = row.id;
          }
        }
      } else {
        table.setRowSelection((old) => {
          const next = { ...old };
          if (value) {
            next[row.id] = true;
          } else {
            delete next[row.id];
          }
          return next;
        });
      }

      // Update the anchor on non-shift clicks, or when there's no anchor yet,
      // so the user can shift-click multiple times from the same starting row.
      if (lastSelectedIdRef && (!shiftKey || lastSelectedIdRef.current == null)) {
        lastSelectedIdRef.current = row.id;
      }
    },
    [groupRows, lastSelectedIdRef, row, table, isSelectAll, setSelectAll],
  );

  // Render an empty cell (no checkbox) when the row isn't selectable.
  if (omitCheckbox) {
    return <Box alignSelf="stretch" fill className="h-full" />;
  }

  return (
    <Box alignSelf="stretch"
      fill
      direction="row"
      align="center"
      justify="center"
      className="h-full"
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        // A press on the checkbox itself is handled by `onCheckedChange`; the
        // cell's own press is the padding around it. The checkbox relays its
        // press as a second click on its hidden `<input>` sibling, which
        // bubbles here too — one tap must never count as two presses.
        if (e.target instanceof Element && e.target.closest('[role="checkbox"], input')) return;
        // The click a held press produced arming the row is spent on the
        // reveal; a tap in the lane selects.
        if (pressArmedClosest(e.currentTarget)) return;
        handleSelectionChange(!isSelected, e.shiftKey);
      }}
      // The whole cell toggles selection, so a row-level `onRowClick` must
      // not also fire from its padding (see ROW_CLICK_CLAIMED_SELECTOR).
      data-row-click-stop=""
    >
      <Box
        width={16}
        height={16}
        align="center"
        justify="center"
        // Hover-revealed on the row scope while unchecked (a checked box
        // stands). On a coarse pointer the first tap on a row reveals it with
        // the rest of the row chrome and the second tap selects — the same
        // reveal as the actions kebab, so no row wears a stray checkbox. The
        // `group-has-…/row` rung keeps the checkbox visible while a popup
        // anchored anywhere in the row is open.
        hoverReveal={hideUntilHover ? 'row' : undefined}
        className={hideUntilHover ? 'group-has-[[data-popup-open]]/row:opacity-100' : undefined}
      >
        <Checkbox
          checked={isSelected}
          // The release of the held press that armed the row reaches the
          // box too where the pointer is fine (a tablet emulation) — spent,
          // like every other handler in the row.
          onCheckedChange={(value, eventDetails) => {
            const target = eventDetails.event.target;
            if (target instanceof Element && pressArmedClosest(target)) return;
            handleSelectionChange(!!value, eventShiftKey(eventDetails.event));
          }}
          aria-label="Select row"
        />
      </Box>
    </Box>
  );
}

/**
 * Creates a selection column definition for row selection.
 *
 * The header checkbox toggles all visible rows (current page when paginated).
 *
 * @example
 * ```tsx
 * const columns: DataTableColumnDef<Payment>[] = [
 *   createSelectionColumn<Payment>(),
 *   { accessorKey: 'email', header: 'Email' },
 * ];
 * ```
 */
export function createSelectionColumn<TData>(): DataTableColumnDef<TData, unknown> {
  const columnDef: DataTableColumnDef<TData, unknown> = {
    id: 'select',
    header: ({ table }) => <SelectionHeader table={table} />,
    // The grip rides the cell's leading gutter (absolute, left edge) so the
    // checkbox keeps its own press; it renders nothing outside a `rowDrag` table.
    cell: ({ row, table }) => (
      <>
        <DataTableRowDragGrip row={row} />
        <SelectionCell row={row} table={table} />
      </>
    ),
    enableSorting: false,
    enableHiding: false,
    meta: {
      width: SELECTION_COLUMN_WIDTH,
    },
  };
  return columnDef;
}
